import React, { useEffect, useState } from "react";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Landmark, ShieldCheck, Home, Plus } from "lucide-react";

const CAT_ICON = { "HR Certificates": FileText, "BIR": Landmark, "SSS": ShieldCheck, "Pag-IBIG": Home };
const STATUS_BADGE = { submitted: "badge badge-pending", processing: "badge badge-info", ready: "badge badge-approved", released: "badge badge-approved", rejected: "badge badge-destructive" };

export default function MyRequests() {
    const [catalog, setCatalog] = useState([]);
    const [mine, setMine] = useState([]);
    const [requesting, setRequesting] = useState(null); // doc type object
    const [form, setForm] = useState({});
    const [chooser, setChooser] = useState(false);

    const load = () => {
        api.get("/document-requests/catalog").then((r) => setCatalog(r.data.categories || [])).catch(() => {});
        api.get("/document-requests/mine").then((r) => setMine(r.data.items || [])).catch(() => setMine([]));
    };
    useEffect(() => { load(); }, []);

    const open = (t) => {
        const init = {};
        t.fields.forEach((f) => { if (f.type === "select") init[f.key] = f.options[0]; });
        setForm(init); setRequesting(t);
    };
    const submit = async () => {
        for (const f of requesting.fields) {
            if (form[f.key] == null || form[f.key] === "") return toast.error(`${f.label} is required`);
        }
        try {
            await api.post("/document-requests", { doc_type: requesting.key, details: form });
            toast.success("Request submitted — HR will prepare your document");
            setRequesting(null); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader title="Certificates & Forms" subtitle="Request employment certificates and government forms. HR receives your request pre-filled — you'll be notified when it's ready for pickup."
                actions={
                    <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" onClick={() => setChooser(true)} data-testid="create-request-btn">
                        <Plus className="size-4" /> Create Request
                    </button>
                } />

            <Dialog open={chooser} onOpenChange={setChooser}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>What do you need?</DialogTitle></DialogHeader>
                    <div className="space-y-3 max-h-[60vh] overflow-auto">
                        {catalog.map((cat) => (
                            <div key={cat.name}>
                                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{cat.name}</div>
                                <div className="space-y-1">
                                    {cat.types.map((t) => (
                                        <button key={t.key} className="w-full text-left border border-border rounded-md px-3 py-2 text-sm hover:border-primary hover:bg-primary/5"
                                            onClick={() => { setChooser(false); open(t); }} data-testid={`choose-${t.key}`}>
                                            {t.label}
                                            {t.official_form && <span className="text-[10px] text-muted-foreground block">{t.official_form}</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {catalog.map((cat) => {
                const Icon = CAT_ICON[cat.name] || FileText;
                return (
                    <div key={cat.name} className="mb-5">
                        <h3 className="font-display font-semibold mb-2 flex items-center gap-2"><Icon className="size-4 text-primary" /> {cat.name}</h3>
                        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                            {cat.types.map((t) => (
                                <Surface key={t.key} className="p-3 flex items-center justify-between gap-2" data-testid={`doc-${t.key}`}>
                                    <div>
                                        <div className="text-sm font-medium">{t.label}</div>
                                        {t.official_form && <div className="text-[10px] text-muted-foreground">{t.official_form}</div>}
                                    </div>
                                    <button onClick={() => open(t)} className="h-8 px-3 rounded-md bg-primary text-white text-xs font-medium shrink-0" data-testid={`request-${t.key}`}>Request</button>
                                </Surface>
                            ))}
                        </div>
                    </div>
                );
            })}

            <h3 className="font-display font-semibold mb-2 mt-6">My requests</h3>
            {!mine.length && <Surface className="p-5"><Empty title="No requests yet" /></Surface>}
            {mine.length > 0 && (
                <TableWrap>
                    <thead><tr><th>Document</th><th>Details</th><th>Requested</th><th>Status</th></tr></thead>
                    <tbody>
                        {mine.map((r) => (
                            <tr key={r.id} data-row-id={r.id}>
                                <td className="font-medium">{r.doc_label}</td>
                                <td className="text-xs text-muted-foreground">{Object.entries(r.details || {}).filter(([, v]) => v).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ") || "—"}</td>
                                <td className="text-xs">{formatDateTime(r.requested_at)}</td>
                                <td>
                                    <span className={STATUS_BADGE[r.status] || "badge"}>{r.status}</span>
                                    {r.status === "released" && <span className="text-[10px] text-emerald-600 block">Ready for pickup — signed by HR</span>}
                                    {r.status === "rejected" && r.note && <span className="text-[10px] text-muted-foreground block">“{r.note}”</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </TableWrap>
            )}

            <Dialog open={!!requesting} onOpenChange={(v) => !v && setRequesting(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{requesting?.label} — Request</DialogTitle></DialogHeader>
                    {requesting && (
                        <div className="space-y-2">
                            {requesting.fields.map((f) => (
                                <div key={f.key}>
                                    <Label>{f.label}</Label>
                                    {f.type === "select" ? (
                                        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`}>
                                            {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    ) : (
                                        <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
                                    )}
                                </div>
                            ))}
                            <div className="text-xs text-muted-foreground">HR receives this pre-filled with your employment records — no need to fill the official form yourself.</div>
                        </div>
                    )}
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submit} data-testid="submit-request">Submit</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
